// ManualIdVerificationPage — port of manual-id-verification.component.{ts,html}
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import Swal from 'sweetalert2';
import {
  useManualIdVerificationsQuery,
  useManualIdStatsQuery,
} from './api';
import {
  IdentityVerificationStatusEnum,
  ManualIdVerificationStatusEnum,
} from '@/types/manual-id';
import type { ManualIdVerificationRecord } from '@/types/manual-id';

function formatDate(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

const today = new Date();
const defaultStart = formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
const defaultEnd = formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 1));

type StatusFilter = ManualIdVerificationStatusEnum;

const STATUS_FILTERS: { value: StatusFilter; labelKey: string; defaultLabel: string }[] = [
  {
    value: ManualIdVerificationStatusEnum.VERIFIED,
    labelKey: 'pages.manualIdVerification.filterVerified',
    defaultLabel: 'Verified',
  },
  {
    value: ManualIdVerificationStatusEnum.REJECTED,
    labelKey: 'pages.manualIdVerification.filterRejected',
    defaultLabel: 'Rejected',
  },
  {
    value: ManualIdVerificationStatusEnum.BLOCKED,
    labelKey: 'pages.manualIdVerification.filterAskingForHelp',
    defaultLabel: 'Asking for help',
  },
  {
    value: ManualIdVerificationStatusEnum.PENDING,
    labelKey: 'pages.manualIdVerification.filterPending',
    defaultLabel: 'Pending',
  },
];

function getStatusClass(status: string): string {
  switch (status.toUpperCase()) {
    case IdentityVerificationStatusEnum.NOT_VERIFIED:
    case IdentityVerificationStatusEnum.IN_PROGRESS:
      return 'mode_done';
    case IdentityVerificationStatusEnum.BLOCKED:
      return 'mode_process';
    case IdentityVerificationStatusEnum.REJECTED:
      return 'mode_off';
    case IdentityVerificationStatusEnum.APPROVED_VERIFIED:
      return 'mode_on';
    default:
      return '';
  }
}

function useStatusLabel() {
  const { t } = useTranslation();
  return (status: string): string => {
    switch (status.toUpperCase()) {
      case IdentityVerificationStatusEnum.APPROVED_VERIFIED:
        return t('global.identityVerificationStatus.verified', { defaultValue: 'Verified' });
      case IdentityVerificationStatusEnum.IN_PROGRESS:
        return t('global.identityVerificationStatus.in_progress', { defaultValue: 'In progress' });
      case IdentityVerificationStatusEnum.NOT_VERIFIED:
        return t('global.identityVerificationStatus.not_verified', {
          defaultValue: 'Not verified',
        });
      case IdentityVerificationStatusEnum.REJECTED:
        return t('global.identityVerificationStatus.rejected', { defaultValue: 'Rejected' });
      case IdentityVerificationStatusEnum.BLOCKED:
        return t('global.identityVerificationStatus.need_help', { defaultValue: 'Need help' });
      default:
        return status;
    }
  };
}

interface StatCardProps {
  value: number | undefined;
  contentKey: string;
  contentDefault: string;
  titleKey: string;
  titleDefault: string;
  color: string;
  icon: React.ReactNode;
}

function StatCard({ value, contentKey, contentDefault, titleKey, titleDefault, color, icon }: StatCardProps) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent>
        <Typography variant="h4" sx={{ color }}>{value ?? '—'}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t(contentKey, { defaultValue: contentDefault })}
        </Typography>
      </CardContent>
      <Box sx={{ bgcolor: color, px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ color: '#fff' }}>
          {t(titleKey, { defaultValue: titleDefault })}
        </Typography>
        <Box sx={{ color: '#fff', display: 'flex' }}>{icon}</Box>
      </Box>
    </Card>
  );
}

export function ManualIdVerificationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getStatusLabel = useStatusLabel();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    ManualIdVerificationStatusEnum.BLOCKED,
  );
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [textFilter, setTextFilter] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const recordsQuery = useManualIdVerificationsQuery({
    startDate,
    endDate,
    status: statusFilter,
  });
  const statsQuery = useManualIdStatsQuery();
  const stats = statsQuery.data;

  if (recordsQuery.isError) {
    void Swal.fire(
      t('messages.error', { defaultValue: 'Error' }),
      (recordsQuery.error as Error).message,
      'error',
    );
  }

  const filtered = useMemo(() => {
    const records: ManualIdVerificationRecord[] = recordsQuery.data ?? [];
    if (!textFilter.trim()) return records;
    const term = textFilter.toLowerCase();
    return records.filter((r) => {
      const f = r.fields;
      return (
        f.Email.toLowerCase().includes(term) ||
        f['First Name'].toLowerCase().includes(term) ||
        f['Last Name'].toLowerCase().includes(term) ||
        f.Role.toLowerCase().includes(term) ||
        f['Id Document Verification Status'].toLowerCase().includes(term) ||
        (f['Company Name'] ?? '').toLowerCase().includes(term)
      );
    });
  }, [recordsQuery.data, textFilter]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleStatusFilter = (s: StatusFilter) => {
    setStatusFilter(s);
    setTextFilter('');
    setPage(0);
  };

  const handleRowClick = (record: ManualIdVerificationRecord) => {
    navigate('/manual-id-verification/user-info', { state: { data: record } });
  };

  return (
    <Box className="container-fluid page" sx={{ p: 2 }}>
      <Box className="d-flex page__box p-3 mt-2">
        <Typography variant="h6">
          {t('menus.menuManualIdVerification', { defaultValue: 'Manual ID Verification' })}
        </Typography>
      </Box>

      <Box className="page__content shadow p-3 position-relative" sx={{ mt: 2 }}>
        {/* Stats row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 2,
            mb: 3,
          }}
        >
          <StatCard
            value={stats?.verified}
            contentKey="pages.manualIdVerification.widget1.content"
            contentDefault="Verified users"
            titleKey="pages.manualIdVerification.widget1.title"
            titleDefault="Verified"
            color="#2ed8b6"
            icon={<CheckCircleOutlineIcon />}
          />
          <StatCard
            value={stats?.rejected}
            contentKey="pages.manualIdVerification.widget2.content"
            contentDefault="Rejected"
            titleKey="pages.manualIdVerification.widget2.title"
            titleDefault="Rejected"
            color="#FF5370"
            icon={<CancelIcon />}
          />
          <StatCard
            value={stats?.needHelp}
            contentKey="pages.manualIdVerification.widget3.content"
            contentDefault="Asking for help"
            titleKey="pages.manualIdVerification.widget3.title"
            titleDefault="Need help"
            color="#FFB64D"
            icon={<HelpOutlineIcon />}
          />
          <StatCard
            value={stats?.waitingForValidation}
            contentKey="pages.manualIdVerification.widget4.content"
            contentDefault="Waiting for validation"
            titleKey="pages.manualIdVerification.widget4.title"
            titleDefault="Waiting"
            color="#a0a0a0"
            icon={<HourglassEmptyIcon />}
          />
        </Box>

        {/* Filters */}
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <ButtonGroup variant="outlined" size="small">
            {STATUS_FILTERS.map((sf) => (
              <Button
                key={sf.value}
                variant={statusFilter === sf.value ? 'contained' : 'outlined'}
                onClick={() => handleStatusFilter(sf.value)}
              >
                {t(sf.labelKey, { defaultValue: sf.defaultLabel })}
              </Button>
            ))}
          </ButtonGroup>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextField
            label={t('pages.manualIdVerification.startDate', { defaultValue: 'Start date' })}
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t('pages.manualIdVerification.endDate', { defaultValue: 'End date' })}
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => { setPage(0); void recordsQuery.refetch(); }}
          >
            {t('global.buttons.apply', { defaultValue: 'Apply' })}
          </Button>
          <TextField
            label="Filter"
            size="small"
            value={textFilter}
            onChange={(e) => { setTextFilter(e.target.value); setPage(0); }}
            sx={{ ml: 'auto !important' }}
          />
        </Stack>

        {/* Table */}
        {recordsQuery.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('pages.manualIdVerification.table.header.email', { defaultValue: 'Email' })}</TableCell>
                  <TableCell>{t('pages.manualIdVerification.table.header.firstName', { defaultValue: 'First name' })}</TableCell>
                  <TableCell>{t('pages.manualIdVerification.table.header.lastName', { defaultValue: 'Last name' })}</TableCell>
                  <TableCell>{t('pages.manualIdVerification.table.header.profile', { defaultValue: 'Profile' })}</TableCell>
                  <TableCell>{t('pages.manualIdVerification.table.header.businessName', { defaultValue: 'Business name' })}</TableCell>
                  <TableCell>{t('pages.manualIdVerification.table.header.status', { defaultValue: 'Status' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((record) => {
                  const status = record.fields['Id Document Verification Status'];
                  return (
                    <TableRow
                      key={record.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(record)}
                    >
                      <TableCell>{record.fields.Email}</TableCell>
                      <TableCell>{record.fields['First Name']}</TableCell>
                      <TableCell>{record.fields['Last Name']}</TableCell>
                      <TableCell>{record.fields.Role}</TableCell>
                      <TableCell>{record.fields['Company Name'] ?? ''}</TableCell>
                      <TableCell>
                        {status && (
                          <span className={`mode ${getStatusClass(status)}`}>
                            {getStatusLabel(status)}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paged.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('global.noData', { defaultValue: 'No records found.' })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
              onPageChange={(_, p) => setPage(p)}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} / ${count}`
              }
            />
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
